import {
  AccessTokenAPIError,
  AccessTokensData,
} from '@squareup/dex-store-access-shared-main-use-access-tokens-hook';
import { ApplicationType } from '@squareup/dex-types-shared-application-type';
import { SandboxToggle } from '@squareup/dex-ui-dex-tech-main-sandbox-toggle';
import {
  ApiExplorerAccessTokenContext,
  useApiExplorerContext,
} from '@squareup/dex-ui-shared-api-explorer-providers';
import { Box, Paragraph20, Heading10 } from '@squareup/dex-ui-shared-base';
import LoadingSpinner from '@squareup/dex-ui-shared-loading-spinner';
import {
  MarketDivider,
  MarketLink,
  MarketPopover,
} from '@squareup/dex-ui-shared-market';
import {
  getNextPublicBaseURL,
  getNextPublicSquareURL,
} from '@squareup/dex-utils-environment';
import { usePathname } from '@squareup/dex-utils-shared-routing-hooks';
import React, { FC, useCallback } from 'react';

import PopoverContent from './PopoverContent';
import styles from './access-token-popover.module.css';

type Props = {
  accessTokens: AccessTokensData[keyof AccessTokensData] | undefined;
  environment: ApplicationType;
  errors: AccessTokenAPIError[];
  isAuthenticated: boolean;
  isLoading: boolean;
  onTokenSelect(_: ApiExplorerAccessTokenContext): void;
  selectedToken?: string;
  supportedEnvironments?: ApplicationType[];
};

const strings = {
  accessTokens: 'Access Tokens',
  signInDescription:
    'Sign in to populate your access tokens from the developer dashboard',
  signIn: 'Sign in',
};

const SignInPopoverContent: FC = () => {
  const { pathName } = usePathname();

  const loginUrl = `${getNextPublicSquareURL()}/login?app=developer&return_to=${getNextPublicBaseURL()}${pathName}`;
  return (
    <Box padding={{ vertical: '3x', horizontal: '4x' }}>
      <Heading10>{strings['accessTokens']}</Heading10>
      <Paragraph20>
        {strings['signInDescription']}&nbsp;
        <MarketLink trackingId="access-token-field-sign-in" href={loginUrl}>
          {strings['signIn']}
        </MarketLink>
      </Paragraph20>
    </Box>
  );
};

const AccessTokenPopover: FC<Props> = ({
  accessTokens,
  environment,
  errors,
  isAuthenticated,
  isLoading,
  onTokenSelect,
  selectedToken,
  supportedEnvironments = [ApplicationType.Sandbox, ApplicationType.Production],
}) => {
  const { dispatchAccessTokenAction } = useApiExplorerContext();

  const onEnvironmentChange = useCallback(
    (environment: ApplicationType) => {
      dispatchAccessTokenAction({
        type: 'SET_ENVIRONMENT',
        payload: environment,
      });
    },
    [dispatchAccessTokenAction]
  );

  function renderPopoverContent() {
    if (!isAuthenticated) {
      return <SignInPopoverContent />;
    }
    if (isLoading) {
      return (
        <Box margin={{ vertical: '3x' }}>
          <LoadingSpinner testId="loading-spinner" />
        </Box>
      );
    }
    return (
      <PopoverContent
        accessTokenData={accessTokens}
        environment={environment}
        errors={errors}
        onTokenSelect={onTokenSelect}
        selectedToken={selectedToken}
      />
    );
  }

  return (
    <MarketPopover slot="popover" className={styles['popover']}>
      <Box margin={{ vertical: '1.5x' }}>
        <SandboxToggle
          onChange={onEnvironmentChange}
          supportedEnvironments={supportedEnvironments}
          value={environment}
        />
      </Box>
      <MarketDivider size="thin" margin="small" />
      {renderPopoverContent()}
    </MarketPopover>
  );
};

export { AccessTokenPopover };
